import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, Search, Plus, Briefcase, User as UserIcon } from 'lucide-react';
import { useChatContext } from '../../contexts/ChatContext';
import * as chatApi from '../../api/chat';
import DMChat from './DMChat';
import { format, isToday, isYesterday } from 'date-fns';

const AVATAR_COLORS = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
function getUserColor(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatLastTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isToday(d)) return format(d, 'h:mm a');
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'MMM d');
  } catch { return ''; }
}

export default function ChatInbox() {
  const navigate = useNavigate();
  const { globalUnreadCount, refreshUnread } = useChatContext();
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showNewDM, setShowNewDM] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [activeDMChannel, setActiveDMChannel] = useState(null);
  const dropdownRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handle(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setActiveDMChannel(null);
        setShowNewDM(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Load channels when opening
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    chatApi.getChannels()
      .then(data => setChannels(data.channels || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  // User search for new DM
  useEffect(() => {
    if (!showNewDM || !userSearch.trim()) { setUserResults([]); return; }
    const timer = setTimeout(() => {
      chatApi.searchUsers(userSearch).then(data => setUserResults(data.users || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, showNewDM]);

  const handleStartDM = async (userId) => {
    try {
      const data = await chatApi.createDMChannel(userId);
      setActiveDMChannel(data.channel);
      setShowNewDM(false);
      // Refresh channels list
      chatApi.getChannels().then(d => setChannels(d.channels || [])).catch(() => {});
    } catch (e) { /* ignore */ }
  };

  const handleChannelClick = (ch) => {
    if (ch.type === 'record' && ch.record_id) {
      // Navigate to record and close inbox — the record page opens the chat
      // We need the record type slug, but we don't have it. Use a direct URL approach:
      setOpen(false);
      // Navigate to the record — we need to figure out the typeSlug from record_id
      // The channel has record_title and record_reference but not the slug
      // For now, navigate to a known fallback — the record detail page will handle it
      navigate(`/records/mva-cases/${ch.record_id}`);
    } else if (ch.type === 'dm') {
      setActiveDMChannel(ch);
    }
  };

  const filtered = channels.filter(ch => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (ch.name || '').toLowerCase().includes(s) ||
      (ch.record_title || '').toLowerCase().includes(s) ||
      (ch.record_reference || '').toLowerCase().includes(s);
  });

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button onClick={() => { setOpen(!open); if (!open) refreshUnread(); }}
        className="relative text-gray-400 hover:text-gray-600" title="Chat">
        <MessageSquare className="h-5 w-5" />
        {globalUnreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center
                           bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
            {globalUnreadCount > 99 ? '99+' : globalUnreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[520px] flex flex-col overflow-hidden">
          {/* DM Chat view */}
          {activeDMChannel ? (
            <div className="flex flex-col h-[480px]">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                <button onClick={() => setActiveDMChannel(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
                <h3 className="text-sm font-semibold text-gray-800 truncate">{activeDMChannel.name}</h3>
              </div>
              <div className="flex-1 overflow-hidden">
                <DMChat channelId={activeDMChannel.id} />
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Messages</h3>
                <div className="flex items-center gap-1">
                  <button onClick={() => setShowNewDM(!showNewDM)}
                    className="p-1 text-gray-400 hover:text-blue-500 rounded" title="New Message">
                    <Plus className="h-4 w-4" />
                  </button>
                  <button onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* New DM user search */}
              {showNewDM && (
                <div className="px-3 py-2 border-b border-gray-100 bg-blue-50">
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                    <Search className="h-3.5 w-3.5 text-gray-400" />
                    <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                      placeholder="Search people to message..."
                      className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" autoFocus />
                  </div>
                  {userResults.length > 0 && (
                    <div className="mt-1.5 max-h-32 overflow-y-auto">
                      {userResults.map(u => (
                        <button key={u.id} onClick={() => handleStartDM(u.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-blue-100 rounded">
                          <div className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
                            style={{ backgroundColor: getUserColor(u.id) }}>
                            {u.first_name[0]}{u.last_name[0]}
                          </div>
                          <span className="text-gray-700">{u.first_name} {u.last_name}</span>
                          <span className="text-xs text-gray-400 ml-auto">{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Search */}
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                  <Search className="h-3.5 w-3.5 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Filter conversations..."
                    className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" />
                </div>
              </div>

              {/* Channel list */}
              <div className="flex-1 overflow-y-auto">
                {loading && <div className="text-center text-xs text-gray-400 py-6">Loading...</div>}
                {!loading && filtered.length === 0 && (
                  <div className="text-center text-xs text-gray-400 py-6">No conversations yet</div>
                )}
                {filtered.map(ch => (
                  <button key={ch.id} onClick={() => handleChannelClick(ch)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b border-gray-50 last:border-0">
                    {/* Icon */}
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      ch.type === 'record' ? 'bg-blue-50 text-blue-500' : 'bg-purple-50 text-purple-500'
                    }`}>
                      {ch.type === 'record' ? <Briefcase className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {ch.record_title || ch.name || 'Chat'}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                          {formatLastTime(ch.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 truncate">
                          {ch.last_message_content
                            ? `${ch.last_message_sender_first || ''}: ${ch.last_message_content}`.slice(0, 60)
                            : 'No messages yet'
                          }
                        </p>
                        {ch.unread_count > 0 && (
                          <span className="ml-2 min-w-[18px] h-[18px] flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full px-1 flex-shrink-0">
                            {ch.unread_count}
                          </span>
                        )}
                      </div>
                      {ch.record_reference && (
                        <span className="text-[10px] text-gray-400 font-mono">{ch.record_reference}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
